import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, ChevronRight, LogOut, MessageCircle, Store } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { selectShop, logout } from './authSlice';
import { Alert, Avatar, Badge, Button, IconButton, IconTile, SearchPill } from '@/components/ui';
import { Splash } from '@/components/layout/Splash';
import { Shop } from '@/services/types';
import { STORAGE_KEYS, SUPPORT_WHATSAPP } from '@/utils/constants';

const lastOpenedShopId = (): number | null => {
    const raw = localStorage.getItem(STORAGE_KEYS.LAST_SHOP_ID);
    const id = raw ? Number(raw) : NaN;
    return Number.isFinite(id) ? id : null;
};

/**
 * 03 Pilih toko. ≤3 shops → cards; more → search + compact rows. Auto-redirects when a shop is already selected.
 */
const ShopSelector: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { user, shops, selectedShop, isLoading, error, isInitialized } = useAppSelector((state) => state.auth);
    const [query, setQuery] = useState('');
    const lastId = useMemo(lastOpenedShopId, []);

    React.useEffect(() => {
        if (isInitialized && selectedShop) navigate('/catalog');
    }, [selectedShop, isInitialized, navigate]);

    const handleSelectShop = (shop: Shop) => {
        dispatch(selectShop(shop));
        toast.success(t('auth.shopSelected', { name: shop.shopName }));
        navigate('/catalog');
    };

    const handleLogout = async () => {
        await dispatch(logout()).unwrap();
        toast.success(t('auth.loggedOut'));
        navigate('/login');
    };

    if (!isInitialized) return <Splash />;
    if (!user) { navigate('/login'); return null; }

    const many = shops.length > 3;
    const q = query.trim().toLowerCase();
    const shown = q ? shops.filter((s) => `${s.shopName} ${s.address ?? ''} ${s.shopIdentifier}`.toLowerCase().includes(q)) : shops;
    const supportHref = `https://wa.me/${SUPPORT_WHATSAPP.number}?text=${encodeURIComponent(t('support.prefill'))}`;
    const supportButton = <Button variant="ghost" size="sm" icon={<MessageCircle />} onClick={() => window.open(supportHref, '_blank', 'noopener')}>{t('support.contact')}</Button>;

    return (
        <div className="flex h-full min-h-0 flex-1 flex-col bg-surface-page lg:p-[32px_56px_36px]">
            <header className="flex items-center gap-3 px-5 pt-4 lg:px-0 lg:pt-0">
                <Avatar name={user.fullname} size="md" />
                <div className="min-w-0 flex-1">
                    <p className="text-[13px] text-ink-muted">{t('auth.hello')}</p>
                    <p className="truncate text-[17px] font-black">{user.fullname || 'User'}</p>
                </div>
                <div className="hidden lg:block">{supportButton}</div>
                <IconButton variant="card" label={t('auth.logout')} onClick={handleLogout} disabled={isLoading}><LogOut /></IconButton>
            </header>

            <div className="flex min-h-0 flex-1 flex-col lg:justify-center lg:gap-7">
                <div className="flex flex-col gap-2 px-5 pt-7 lg:flex-row lg:items-end lg:gap-4 lg:px-0 lg:pt-0">
                    <div className="flex-1">
                        <h1 className="text-[30px] font-black tracking-[-0.02em] text-ink-brand lg:text-[40px]">{t('auth.selectShop')}</h1>
                        <p className="text-[15px] text-ink-secondary lg:text-base">{t('auth.shopAccessCount', { count: shops.length })}</p>
                    </div>
                    {many && <SearchPill size="lg" value={query} onChange={setQuery} placeholder={t('auth.searchShop')} className="mt-4 !flex-none lg:mt-0 lg:w-[380px]" />}
                </div>

                {error && <Alert variant="error" message={error} className="mx-5 mt-4 lg:mx-0 lg:mt-0" />}
                {shops.length === 0 && !isLoading && <Alert variant="info" message={t('auth.noShops')} className="mx-5 mt-4 lg:mx-0" />}

                <div className={`min-h-0 flex-1 overflow-y-auto px-5 pt-5 lg:flex-none lg:px-0 lg:pt-0 ${many ? 'flex flex-col gap-2 lg:grid lg:grid-cols-3 lg:gap-3' : 'flex flex-col gap-3 lg:grid lg:grid-cols-3 lg:gap-5'}`}>
                    {shown.map((shop) => {
                        const last = shop.shopId === lastId;
                        const sub = shop.address || shop.shopIdentifier;
                        if (many) {
                            return (
                                <button key={shop.shopId} type="button" onClick={() => handleSelectShop(shop)} className={`flex h-16 items-center gap-3 rounded-lg bg-surface-card px-3.5 text-left ${last ? 'border-2 border-accent-500 shadow-md lg:h-[76px]' : 'border border-line-subtle shadow-sm lg:h-[76px]'}`}>
                                    <IconTile size={44} tone={last ? 'accentSoft' : 'sunken'} className={last ? '' : '!text-ink-brand'}><Store /></IconTile>
                                    <span className="flex min-w-0 flex-1 flex-col">
                                        <span className="truncate text-[15px] font-black">{shop.shopName}</span>
                                        <span className={`truncate text-xs ${last ? 'font-extrabold text-ink-accent' : 'text-ink-muted'}`}>{last ? t('auth.lastOpened') : sub}</span>
                                    </span>
                                    <ChevronRight className={`h-[18px] w-[18px] ${last ? 'text-ink-accent' : 'text-ink-muted'}`} />
                                </button>
                            );
                        }
                        return (
                            <button key={shop.shopId} type="button" onClick={() => handleSelectShop(shop)} className={`flex items-center gap-3.5 rounded-lg bg-surface-card p-4 text-left lg:min-h-[230px] lg:flex-col lg:items-stretch lg:gap-4 lg:rounded-xl lg:p-6 ${last ? 'border-2 border-accent-500 shadow-md' : 'border border-line-subtle shadow-sm'}`}>
                                <div className="flex items-start justify-between">
                                    <IconTile size={56} tone={last ? 'accentSoft' : 'brandSoft'} className="lg:!h-16 lg:!w-16"><Store /></IconTile>
                                    {last && <Badge tone="accent" className="hidden lg:inline-flex">{t('auth.lastOpened')}</Badge>}
                                </div>
                                <span className="flex min-w-0 flex-1 flex-col gap-0.5 lg:justify-end">
                                    <span className="truncate text-base font-black lg:text-xl">{shop.shopName}</span>
                                    <span className="truncate text-[13px] text-ink-muted lg:text-sm">{sub}</span>
                                    <span className="mt-1 flex items-center gap-2 lg:hidden">
                                        {last && <Badge tone="accent">{t('auth.lastOpened')}</Badge>}
                                        <Badge tone="neutral">{shop.role}</Badge>
                                    </span>
                                </span>
                                <ChevronRight className="h-5 w-5 text-ink-muted lg:hidden" />
                                <span className={`hidden items-center gap-1.5 text-sm lg:flex ${last ? 'font-black text-ink-accent' : 'font-extrabold text-ink-muted'}`}>
                                    {t('auth.openShop')} <ArrowRight className="h-4 w-4" />
                                </span>
                            </button>
                        );
                    })}
                </div>
            </div>

            <footer className="flex justify-center px-5 pb-[max(28px,env(safe-area-inset-bottom))] pt-3 lg:hidden">{supportButton}</footer>
        </div>
    );
};

export default ShopSelector;
